/**
 * Stack navigation for expert-review — push/pop/replace and `cameFrom` stack state.
 *
 * Belongs here: route helpers, feature leave (`exitExpertReview`), and empty-stack home resolve.
 * Does not belong here: questionnaire session maps or HTTP.
 *
 * Leave model:
 * - `exitExpertReview` — leave the feature; destination from sticky entry `source`
 * - `goToHub` — in-feature home (only valid when strategy status is COMPLETED/SUBMITTED)
 *
 * Empty-stack / unsafe pop uses `resolveExpertReviewHome` (status gate). Exception:
 * strategy entry URL with `isIntro` + `from=APP` → always `exitExpertReview`.
 * Came from hub → `goToHub` (no status gate).
 *
 * Query: never forwarded. Only `goToStrategyQuestionnaire({ showIntro: true })` may set
 * `isIntro` / `from=APP` / `source`. Sticky `source` lives on ExpertReviewStatusContext.
 */
import { useLocation } from 'react-router-dom'
import { goBack as appGoBack, goToPortfolioDashboard } from '../../../../utils/appBridge'
import { STACK_FEATURE } from '../../../../stack-navigation/stackConfig'
import { useStack } from '../../../../stack-navigation/StackProvider'
import { isQuestionnaireStatusDone } from '../shared/types/questionnaire.types'
import {
  ExpertReviewSource,
  useExpertReviewStatusContext
} from '../providers/ExpertReviewStatusContext'
import { EXPERT_REVIEW_ROUTES } from './expertReviewPaths'

/** Where the user was before this screen — use these values, not raw strings. */
export const EXPERT_REVIEW_CAME_FROM = {
  UNKNOWN: 'unknown',
  HUB: 'hub',
  STRATEGY_QUESTIONNAIRE: 'strategy-questionnaire',
  STRATEGY_REVIEW: 'strategy-review',
  RISK_PROFILE: 'risk-profile',
  PREFERENCES_LIST: 'preferences-list',
  PREFERENCES_QUESTIONNAIRE: 'preferences-questionnaire',
  PREFERENCES_REVIEW: 'preferences-review'
} as const

export type ExpertReviewCameFrom =
  (typeof EXPERT_REVIEW_CAME_FROM)[keyof typeof EXPERT_REVIEW_CAME_FROM]

export type ExpertReviewNavigationState = {
  cameFrom: ExpertReviewCameFrom
  /** Current URL has `from=APP` — not carried in stack state; not forwarded to later routes. */
  openedFromApp: boolean
}

const CAME_FROM_VALUES = new Set<string>(Object.values(EXPERT_REVIEW_CAME_FROM))

export function isExpertReviewCameFrom(value: unknown): value is ExpertReviewCameFrom {
  return typeof value === 'string' && CAME_FROM_VALUES.has(value)
}

const FROM_APP = { key: 'from', value: 'APP' } as const
const IS_INTRO = { key: 'isIntro', value: 'true' } as const
const SOURCE = { key: 'source' } as const

export type ExpertReviewNavigation = {
  navigationState: ExpertReviewNavigationState
  canGoBack: boolean
  goToHub: (options?: { replace?: boolean }) => void
  /**
   * Leave expert-review entirely — reset feature stack, then native leave by `source`.
   * Callers that own session state clear drafts before this (hub / menu).
   */
  exitExpertReview: () => void
  goBack: () => void
  goToSubmitPage: () => void
  goToStrategyQuestionnaire: (options?: {
    showIntro?: boolean
    replace?: boolean
    cameFrom?: ExpertReviewCameFrom
  }) => void
  goToStrategyStageEdit: (options: {
    stageId: string
    cameFrom?: ExpertReviewCameFrom
  }) => void
  /** Leave strategy questionnaire route (intro / answering / edit). */
  goBackFromStrategyQuestionnaire: () => void
  goToStrategyReview: (options: { cameFrom: ExpertReviewCameFrom; replace?: boolean }) => void
  goBackFromStrategyReview: () => void
  goToRiskProfile: (options: { cameFrom: ExpertReviewCameFrom; replace?: boolean }) => void
  goBackFromRiskProfile: () => void
  goToPreferencesList: (options?: { replace?: boolean; cameFrom?: ExpertReviewCameFrom }) => void
  goBackFromPreferencesList: () => void
  goToPreferencesQuestionnaire: (options?: {
    cameFrom?: ExpertReviewCameFrom
    replace?: boolean
  }) => void
  goToPreferencesTypeEdit: (options: { preferenceTypeId: string }) => void
  goBackFromPreferencesQuestionnaire: () => void
  goToPreferencesReview: (options: { cameFrom: ExpertReviewCameFrom; replace?: boolean }) => void
  goBackFromPreferencesReview: () => void
}

/** Stack push/pop/replace for the expert-review journey. */
export function useExpertReviewNavigation(): ExpertReviewNavigation {
  const { push, pop, resetFeature, canGoBack, current } = useStack()
  const location = useLocation()
  const search = location.search
  const { statusData, entrySource: source } = useExpertReviewStatusContext()

  const cameFrom = isExpertReviewCameFrom(current?.state?.cameFrom)
    ? current.state.cameFrom
    : EXPERT_REVIEW_CAME_FROM.UNKNOWN
  const searchParams = new URLSearchParams(search)
  const openedFromApp = searchParams.get(FROM_APP.key) === FROM_APP.value

  function goTo(options: { path: string; cameFrom: ExpertReviewCameFrom; replace?: boolean }) {
    // Never attach query — callers pass a full path when query is needed
    push(options.path, {
      replace: options.replace ?? false,
      state: { cameFrom: options.cameFrom }
    })
  }

  function goToHub(options?: { replace?: boolean }) {
    goTo({
      path: EXPERT_REVIEW_ROUTES.root,
      cameFrom: EXPERT_REVIEW_CAME_FROM.UNKNOWN,
      replace: options?.replace ?? false
    })
  }

  /**
   * Leave the feature — reset stack, then native leave from sticky `source`.
   * PORTFOLIO_DASHBOARD (and missing/unknown) → goToPortfolioDashboard.
   * ELITE_LANDING_DASHBOARD / ALLOCATION_PLAN_PAGE → app goBack.
   * Web/dev: native bridges are no-ops without uniAppHandle, so replace to hub root.
   * Callers that own session state clear drafts before this (hub / menu).
   */
  function exitExpertReview() {
    resetFeature(STACK_FEATURE.EXPERT_REVIEW.id, () => {
      console.log('exitExpertReview source', source)
      if (
        source === ExpertReviewSource.ELITE_LANDING_DASHBOARD ||
        source === ExpertReviewSource.ALLOCATION_PLAN_PAGE
      ) {
        appGoBack()
      } else {
        goToPortfolioDashboard()
      }
      if (!isRunningInsideApp()) {
        push(EXPERT_REVIEW_ROUTES.root, {
          replace: true,
          state: { cameFrom: EXPERT_REVIEW_CAME_FROM.UNKNOWN }
        })
      }
    })
  }

  /**
   * Boundary when pop is unsafe: hub only if strategy questionnaire is COMPLETED/SUBMITTED;
   * otherwise leave the feature. Risk profile does not matter.
   * Missing/loading status → not done → exitExpertReview (portfolio).
   */
  function resolveExpertReviewHome() {
    if (isQuestionnaireStatusDone(statusData?.strategyQuestionnaire?.status)) {
      goToHub({ replace: true })
      return
    }
    exitExpertReview()
  }

  /**
   * Generic screen back: pop when there is a real prior screen; else home.
   * Came from hub → hub (no status gate — hub opened this screen).
   * Unknown / empty stack → status boundary (hub only if strategy is done).
   */
  function goBackOrResolveHome() {
    if (cameFrom === EXPERT_REVIEW_CAME_FROM.HUB) {
      goToHub({ replace: true })
      return
    }
    if (canGoBack) {
      pop()
      return
    }
    resolveExpertReviewHome()
  }

  function goBack() {
    goBackOrResolveHome()
  }

  function goToSubmitPage() {
    goTo({ path: EXPERT_REVIEW_ROUTES.review, cameFrom: EXPERT_REVIEW_CAME_FROM.HUB })
  }

  function goToStrategyQuestionnaire(options?: {
    showIntro?: boolean
    replace?: boolean
    cameFrom?: ExpertReviewCameFrom
  }) {
    const strategyPath = EXPERT_REVIEW_ROUTES.strategy.root
    // Only opener that may set query
    let path = strategyPath
    if (options?.showIntro === true) {
      const params = new URLSearchParams()
      params.set(IS_INTRO.key, IS_INTRO.value)
      // Keep from=APP / source only for this intro open if the current URL already has them
      if (openedFromApp) params.set(FROM_APP.key, FROM_APP.value)
      if (source != null) params.set(SOURCE.key, source)
      path = `${strategyPath}?${params.toString()}`
    }
    goTo({
      path,
      cameFrom: options?.cameFrom ?? EXPERT_REVIEW_CAME_FROM.HUB,
      replace: options?.replace ?? false
    })
  }

  function goToStrategyStageEdit(options: {
    stageId: string
    cameFrom?: ExpertReviewCameFrom
  }) {
    goTo({
      path: EXPERT_REVIEW_ROUTES.strategy.editStage(options.stageId),
      cameFrom: options.cameFrom ?? EXPERT_REVIEW_CAME_FROM.STRATEGY_REVIEW
    })
  }

  function goBackFromStrategyQuestionnaire() {
    const params = new URLSearchParams(search)
    // Entry URL still has isIntro after intro dismiss — from=APP on that journey → portfolio
    if (
      params.get(IS_INTRO.key) === IS_INTRO.value &&
      params.get(FROM_APP.key) === FROM_APP.value
    ) {
      exitExpertReview()
      return
    }
    goBackOrResolveHome()
  }

  function goToStrategyReview(options: { cameFrom: ExpertReviewCameFrom; replace?: boolean }) {
    goTo({
      path: EXPERT_REVIEW_ROUTES.strategy.review,
      cameFrom: options.cameFrom,
      replace: options.replace ?? false
    })
  }

  function goBackFromStrategyReview() {
    goBackOrResolveHome()
  }

  function goToRiskProfile(options: { cameFrom: ExpertReviewCameFrom; replace?: boolean }) {
    goTo({
      path: EXPERT_REVIEW_ROUTES.strategy.riskProfile,
      cameFrom: options.cameFrom,
      replace: options.replace ?? false
    })
  }

  function goBackFromRiskProfile() {
    goBackOrResolveHome()
  }

  function goToPreferencesList(options?: { replace?: boolean; cameFrom?: ExpertReviewCameFrom }) {
    goTo({
      path: EXPERT_REVIEW_ROUTES.preferences.list,
      cameFrom: options?.cameFrom ?? EXPERT_REVIEW_CAME_FROM.HUB,
      replace: options?.replace ?? false
    })
  }

  function goBackFromPreferencesList() {
    goBackOrResolveHome()
  }

  function goToPreferencesQuestionnaire(options?: {
    cameFrom?: ExpertReviewCameFrom
    replace?: boolean
  }) {
    goTo({
      path: EXPERT_REVIEW_ROUTES.preferences.questionnaire,
      cameFrom: options?.cameFrom ?? EXPERT_REVIEW_CAME_FROM.PREFERENCES_LIST,
      replace: options?.replace ?? false
    })
  }

  function goToPreferencesTypeEdit(options: { preferenceTypeId: string }) {
    goTo({
      path: EXPERT_REVIEW_ROUTES.preferences.editPreferenceType(options.preferenceTypeId),
      cameFrom: EXPERT_REVIEW_CAME_FROM.PREFERENCES_REVIEW
    })
  }

  function goBackFromPreferencesQuestionnaire() {
    goBackOrResolveHome()
  }

  function goToPreferencesReview(options: {
    cameFrom: ExpertReviewCameFrom
    replace?: boolean
  }) {
    goTo({
      path: EXPERT_REVIEW_ROUTES.preferences.review,
      cameFrom: options.cameFrom,
      replace: options.replace ?? false
    })
  }

  function goBackFromPreferencesReview() {
    goBackOrResolveHome()
  }

  return {
    navigationState: { cameFrom, openedFromApp },
    canGoBack,
    goToHub,
    exitExpertReview,
    goBack,
    goToSubmitPage,
    goToStrategyQuestionnaire,
    goToStrategyStageEdit,
    goBackFromStrategyQuestionnaire,
    goToStrategyReview,
    goBackFromStrategyReview,
    goToRiskProfile,
    goBackFromRiskProfile,
    goToPreferencesList,
    goBackFromPreferencesList,
    goToPreferencesQuestionnaire,
    goToPreferencesTypeEdit,
    goBackFromPreferencesQuestionnaire,
    goToPreferencesReview,
    goBackFromPreferencesReview
  }
}

function isRunningInsideApp(): boolean {
  return Boolean((globalThis as { uniAppHandle?: unknown }).uniAppHandle)
}
